import { FormEvent, useState } from 'react';
import { z } from 'zod';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { FileUpload } from '@components/FileUpload';
import { uploadFile, StoragePaths, createHubRequest } from '@lib/firebase';
import { HubRequestStatus } from '@shared/types/partner';
import { ROUTES } from '@lib/routes';
import { useFormFields } from '@hooks/useFormFields';
import { validateWithSchema } from '@lib/forms';

const hubRequestSchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  contactName: z.string().min(2, 'Contact name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phoneNumber: z.string().min(10, 'Please enter a valid phone number'),
  street: z.string().min(5, 'Please enter a valid street address'),
  city: z.string().min(2, 'Please enter a valid city'),
  state: z.string().min(2, 'Please enter a valid state'),
  zipCode: z.string().min(5, 'Please enter a valid ZIP code'),
  country: z.string().min(2, 'Please enter a valid country'),
  businessType: z.string().min(1, 'Please select a business type'),
  estimatedFootTraffic: z.string().optional(),
  parkingAvailable: z.boolean(),
  indoorStorage: z.boolean(),
  notes: z.string().optional(),
});

type HubRequestFormData = z.infer<typeof hubRequestSchema>;

export function HubRequestPage(): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const {
    values,
    handleChange,
    errors: fieldErrors,
    setErrors,
    setFieldValue,
  } = useFormFields<HubRequestFormData>({
    companyName: '',
    contactName: '',
    email: '',
    phoneNumber: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    businessType: '',
    estimatedFootTraffic: '',
    parkingAvailable: false,
    indoorStorage: false,
    notes: '',
  });

  const handleFilesChange = (files: File[]): void => {
    setUploadedFiles(files);
  };

  const submitRequest = async (data: HubRequestFormData): Promise<void> => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Upload images to Firebase Storage
      const imageUrls: string[] = [];
      const requestId = `hub-request-${Date.now()}`;

      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        if (!file) continue;
        const path = StoragePaths.hubRequestImage(requestId, `site-image-${i}`);
        const url = await uploadFile(path, file, {
          contentType: file.type,
        });
        imageUrls.push(url);
      }

      // TODO: Geocode address to get coordinates
      // For now, we'll leave coordinates undefined
      // You can use Google Geocoding API here

      // Create hub request document
      const hubRequest = {
        companyName: data.companyName,
        contactName: data.contactName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        address: {
          street: data.street,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          country: data.country,
        },
        siteImages: imageUrls,
        businessType: data.businessType,
        estimatedFootTraffic: data.estimatedFootTraffic,
        parkingAvailable: data.parkingAvailable,
        indoorStorage: data.indoorStorage,
        notes: data.notes,
        status: HubRequestStatus.PENDING,
        submittedAt: new Date(),
      };

      // Save to Firestore
      await createHubRequest(hubRequest);

      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to submit request'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const validation = validateWithSchema(hubRequestSchema, values);
    if (!validation.success) {
      setErrors(validation.errors);
      return;
    }
    await submitRequest(validation.data);
  };

  if (submitSuccess) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900">
                <svg
                  className="h-8 w-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Request Submitted!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Thank you for your interest in becoming a partner hub. We've received
              your request and will review it shortly. Our team will contact you at{' '}
              {values.email} within 2-3 business days.
            </p>
            <Button onClick={() => (window.location.href = ROUTES.HOME)}>
              Return to Home
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Become a Partner Hub
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Host e-bikes at your location and earn revenue. Fill out the form below
              to get started.
            </p>
          </div>

          <Card>
            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Company Information */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Company Information
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Company Name"
                    name="companyName"
                    value={values.companyName}
                    onChange={handleChange}
                    error={fieldErrors.companyName}
                  />
                  <Input
                    label="Contact Name"
                    name="contactName"
                    value={values.contactName}
                    onChange={handleChange}
                    error={fieldErrors.contactName}
                  />
                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={fieldErrors.email}
                  />
                  <Input
                    label="Phone Number"
                    type="tel"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    error={fieldErrors.phoneNumber}
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Location Address
                </h2>
                <div className="space-y-4">
                  <Input
                    label="Street Address"
                    name="street"
                    value={values.street}
                    onChange={handleChange}
                    error={fieldErrors.street}
                  />
                  <div className="grid md:grid-cols-3 gap-4">
                    <Input
                      label="City"
                      name="city"
                      value={values.city}
                      onChange={handleChange}
                      error={fieldErrors.city}
                    />
                    <Input
                      label="State"
                      name="state"
                      value={values.state}
                      onChange={handleChange}
                      error={fieldErrors.state}
                    />
                    <Input
                      label="ZIP Code"
                      name="zipCode"
                      value={values.zipCode}
                      onChange={handleChange}
                      error={fieldErrors.zipCode}
                    />
                  </div>
                  <Input
                    label="Country"
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                    error={fieldErrors.country}
                  />
                </div>
              </div>

              {/* Business Details */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Business Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Business Type
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <select
                      name="businessType"
                      value={values.businessType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select business type</option>
                      <option value="hotel">Hotel</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="retail">Retail Store</option>
                      <option value="office">Office Building</option>
                      <option value="resort">Resort</option>
                      <option value="other">Other</option>
                    </select>
                    {fieldErrors.businessType && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {fieldErrors.businessType}
                      </p>
                    )}
                  </div>

                  <Input
                    label="Estimated Daily Foot Traffic (optional)"
                    placeholder="e.g., 100-200 visitors"
                    name="estimatedFootTraffic"
                    value={values.estimatedFootTraffic || ''}
                    onChange={handleChange}
                    error={fieldErrors.estimatedFootTraffic}
                  />
                </div>
              </div>

              {/* Facilities */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Facilities
                </h2>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={values.parkingAvailable}
                      onChange={(e) => setFieldValue('parkingAvailable', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">
                      Parking available for customers
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={values.indoorStorage}
                      onChange={(e) => setFieldValue('indoorStorage', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-gray-700 dark:text-gray-300">
                      Indoor storage space available
                    </span>
                  </label>
                </div>
              </div>

              {/* Site Photos */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Site Photos
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Upload photos of your location, parking area, and storage space (if
                  applicable). This helps us assess your location.
                </p>
                <FileUpload
                  label="Upload Site Photos"
                  accept="image/*"
                  multiple
                  maxFiles={10}
                  maxSizeMB={10}
                  onFilesChange={handleFilesChange}
                />
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Additional Notes (optional)
                </label>
                <textarea
                  name="notes"
                  value={values.notes || ''}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tell us anything else about your location that might be relevant..."
                />
              </div>

              {submitError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                </div>
              )}

              <Button type="submit" fullWidth size="lg" isLoading={isSubmitting}>
                Submit Request
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

